import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Home, BadgeCheck, ArrowUpRight, ZoomIn } from "lucide-react";
import { Reveal } from "./Reveal";
import { INTERIOR_INTRO, INTERIOR_CREDS, INTERIOR_LICENSE, INTERIOR_COUNT } from "../../data/content";

const IMAGES = Array.from({ length: INTERIOR_COUNT }, (_, i) => `/assets/interior/room-${String(i + 1).padStart(2, "0")}.jpg`);

// "Curated Exhibition" asymmetric rhythm across a 12-col grid (repeats every 5).
const SPANS = [
  "md:col-span-8",
  "md:col-span-3 md:col-start-10 md:mt-32",
  "md:col-span-4 md:col-start-2",
  "md:col-span-5 md:col-start-7",
  "md:col-span-10 md:col-start-2",
];

export const InteriorDesign = () => {
  const [active, setActive] = useState(null);

  return (
    <section id="interiors" data-testid="interiors-section" className="mx-auto max-w-[1500px] px-6 md:px-12 py-24 md:py-40">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
        <Reveal className="lg:col-span-5">
          <p className="overline text-terracotta flex items-center gap-2">
            <Home size={14} /> (05.75) Interior Design
          </p>
          <h2 className="mt-6 font-serif text-5xl md:text-7xl tracking-tight leading-[0.95] text-ink">
            Spaces, planned
            <br />
            and elevated.
          </h2>
          <div className="mt-8 flex flex-wrap gap-2.5">
            {INTERIOR_CREDS.map((c) => (
              <span
                key={c}
                className="inline-flex items-center gap-2 rounded-full border border-line bg-paper px-4 py-2 text-sm text-ink"
              >
                <BadgeCheck size={15} className="text-sage" /> {c}
              </span>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.12} className="lg:col-span-7">
          <p className="text-lg md:text-xl text-stone leading-relaxed font-light border-l-2 border-terracotta pl-6">
            {INTERIOR_INTRO}
          </p>

          {/* License */}
          <button
            onClick={() => setActive(INTERIOR_LICENSE)}
            data-testid="interior-license"
            className="group mt-8 flex items-center gap-5 w-full rounded-2xl border border-line bg-sand p-4 md:p-5 text-left hover:border-ink/30 transition-colors duration-300"
          >
            <img
              src={INTERIOR_LICENSE}
              alt="Illinois Real Estate Broker license, address redacted"
              loading="lazy"
              className="w-28 md:w-36 rounded-lg border border-line shrink-0"
            />
            <div className="flex-1">
              <p className="font-serif text-xl md:text-2xl text-ink leading-tight">Illinois Real Estate Broker License</p>
              <p className="mt-1 text-sm text-stone">Active through 2028 · address redacted for privacy</p>
              <span className="mt-2 inline-flex items-center gap-1.5 text-sm text-ink group-hover:text-terracotta transition-colors duration-300">
                View license <ArrowUpRight size={14} />
              </span>
            </div>
          </button>
        </Reveal>
      </div>

      {/* Curated exhibition gallery */}
      <div className="mt-20 grid grid-cols-1 md:grid-cols-12 gap-y-14 md:gap-y-24 gap-x-8">
        {IMAGES.map((src, i) => (
          <motion.div
            key={src}
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className={SPANS[i % SPANS.length]}
          >
            <button
              onClick={() => setActive(src)}
              data-testid={`interior-img-${i + 1}`}
              className="group block w-full cursor-zoom-in text-left"
            >
              <div className="relative overflow-hidden border border-line bg-sand transition-transform duration-700 ease-out group-hover:scale-[0.98]">
                <img
                  src={src}
                  alt={`Interior design project ${i + 1}`}
                  loading="lazy"
                  className="w-full h-auto transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]"
                />
                <span className="absolute inset-0 bg-ink/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <span className="absolute right-4 bottom-4 grid place-items-center w-11 h-11 rounded-full bg-paper/90 text-ink translate-y-1.5 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                  <ZoomIn size={18} />
                </span>
              </div>
              <div className="mt-3 flex items-center justify-between">
                <span className="font-grotesk text-xs uppercase tracking-[0.2em] text-stone">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="font-grotesk text-xs uppercase tracking-[0.2em] text-stone/45 group-hover:text-terracotta transition-colors duration-300">
                  Interior
                </span>
              </div>
            </button>
          </motion.div>
        ))}
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="interior-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setActive(null)}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/90 backdrop-blur-md p-4 md:p-10"
          >
            <button
              data-testid="interior-modal-close"
              onClick={() => setActive(null)}
              className="absolute right-5 top-5 grid place-items-center w-11 h-11 rounded-full bg-paper text-ink hover:bg-terracotta hover:text-paper transition-colors duration-300"
              aria-label="Close"
            >
              <X size={20} />
            </button>
            <motion.img
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              src={active}
              alt="Interior design, full view"
              className="max-h-[85vh] max-w-full w-auto rounded-xl object-contain"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
